<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import _ from 'lodash'
import SiteAdminService, { isPaywallSiteUnconfigured } from '@/services/SiteAdminService'
import { useSiteGiving } from '@/composables/useSiteGiving'
import { formatMoney } from '@/Utils/siteTransactions'
import Spinner from '@/components/ui/Spinner.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSelect from '@/components/Atoms/FormSelect.vue'
import FormPagination from '@/components/Atoms/FormPagination.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import Alert from '@/components/Atoms/Alert.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconSearch from '@/components/icon/IconSearch.vue'
import IconCopy from '@/components/icon/IconCopy.vue'

// Monetize › Donations (broadcast template, native giving): the paywall's
// `site_donations` rows through castr `GET /sites/{id}/giving/donations`
// (sites-broadcast-template-contract.md §5 / §6.3) with the donor's full
// e-mail, and refunds through castr `POST …/donations/{id}/refund`
// (internal/sites-donation-refunds.md). Unlike plan refunds, Castr's platform
// fee goes back to the owner in proportion to the refunded amount; Stripe keeps
// its processing fee. CSV comes later.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})
const { notify } = useNotification()
const giving = useSiteGiving(() => props.siteId)

const PER_PAGE = 25
const STATUSES = [
	{ value: '', text: 'All statuses' },
	{ value: 'succeeded', text: 'Succeeded' },
	{ value: 'partially_refunded', text: 'Partially refunded' },
	{ value: 'refunded', text: 'Refunded' },
	{ value: 'pending', text: 'Pending' },
	{ value: 'failed', text: 'Failed' },
]
const STATUS_LABELS = { succeeded: 'Succeeded', pending: 'Pending', failed: 'Failed', refunded: 'Refunded', partially_refunded: 'Partially refunded' }
const STATUS_VARIANTS = { succeeded: 'success', pending: 'muted', failed: 'warning', refunded: 'warning', partially_refunded: 'warning' }
const REFUND_REASONS = [
	{ value: 'requested_by_customer', text: 'Requested by the donor' },
	{ value: 'duplicate', text: 'Duplicate gift' },
	{ value: 'fraudulent', text: 'Fraudulent' },
]

const loading = ref(true)
const loadError = ref(null)
const rows = ref([])
const meta = ref({ total: 0, last_page: 1 })
const disabledReason = ref(null) // 'donations_disabled' | 'no_product' | 'not_supported' | null
const page = ref(1)
const q = ref('')
const status = ref('')

const load = async () => {
	loading.value = true
	try {
		const params = _.omitBy({ page: page.value, per_page: PER_PAGE, q: q.value.trim() || undefined, status: status.value || undefined }, _.isUndefined)
		const res = await SiteAdminService.listDonations(props.siteId, params)
		rows.value = res?.data || []
		meta.value = res?.meta || { total: rows.value.length, last_page: 1 }
		disabledReason.value = res?.enabled === false ? (res.reason || 'donations_disabled') : null
		loadError.value = null
	} catch (err) {
		rows.value = []
		loadError.value = err
	}
	loading.value = false
}
const debouncedSearch = _.debounce(() => { page.value = 1; load() }, 350)
watch(q, () => debouncedSearch())
watch(status, () => { page.value = 1; load() })
watch(page, () => load())
onMounted(() => { load(); giving.load() })

const unlinked = computed(() => isPaywallSiteUnconfigured(loadError.value))
const filtered = computed(() => Boolean(q.value.trim() || status.value))
const nativeOff = computed(() => giving.state.value.loaded && giving.mode.value !== 'native')

const donorName = (d) => d.viewer?.name || d.name || ''
const donorEmail = (d) => d.viewer?.email || d.email || ''
const donorLabel = (d) => donorName(d) || donorEmail(d) || 'Donor'
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—')
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : '')
const amountLabel = (d) => formatMoney(d.amount, d.currency)

const num = (v) => {
	const n = Number(v)
	return Number.isFinite(n) ? n : 0
}
// castr derives both fields for a paywall without refunds; the fallbacks only cover an older castr.
const refundedOf = (d) => (d.refunded_amount !== undefined && d.refunded_amount !== null ? num(d.refunded_amount) : (d.status === 'refunded' ? num(d.amount) : 0))
const refundableOf = (d) => (d.refundable_amount !== undefined && d.refundable_amount !== null ? num(d.refundable_amount) : (d.status === 'succeeded' ? num(d.amount) : 0))
const canRefund = (d) => ['succeeded', 'partially_refunded'].includes(d.status) && refundableOf(d) > 0
const refundedNote = (d) => `−${formatMoney(refundedOf(d), d.currency)} refunded`
// Long addresses wrap after the "@" (or at a hyphen), never letter by letter.
const emailParts = (email) => {
	const at = email.lastIndexOf('@')
	return at > 0 ? [email.slice(0, at + 1), email.slice(at + 1)] : [email, '']
}
const statusLabel = (d) => (d.status === 'partially_refunded' && refundedOf(d) > 0
	? `Partially refunded ${formatMoney(refundedOf(d), d.currency)}`
	: STATUS_LABELS[d.status] || d.status)

const emptyCopy = computed(() => {
	if (disabledReason.value === 'donations_disabled') return 'Donations are temporarily unavailable on the payment service. Gifts will show here once they are back on.'
	if (disabledReason.value === 'no_product' || nativeOff.value) return 'Turn on Donations under Settings › Broadcast › Giving to receive gifts on your site.'
	if (disabledReason.value === 'not_supported') return 'Donations need a newer payment service version.'
	if (filtered.value) return 'Nothing matches these filters.'
	return 'Gifts show up here as soon as someone gives on your site.'
})

const copyEmail = async (email) => {
	try {
		await navigator.clipboard.writeText(email)
		notify({ group: 'success', text: 'E-mail copied.' })
	} catch {
		notify({ group: 'error', text: 'Could not copy.' })
	}
}

// --- refund ----------------------------------------------------------------------
const fractionDigits = (currency) => {
	try {
		return new Intl.NumberFormat('en', { style: 'currency', currency: String(currency || 'USD').toUpperCase() }).resolvedOptions().maximumFractionDigits
	} catch {
		return 2
	}
}
const refundOpen = ref(false)
const refundRow = ref(null)
const refundMode = ref('full') // 'full' | 'partial'
const refundAmountInput = ref('')
const refundReason = ref('requested_by_customer')
const refunding = ref(false)
const refundError = ref(null)

const refundCurrency = computed(() => refundRow.value?.currency || 'USD')
const refundRemaining = computed(() => (refundRow.value ? refundableOf(refundRow.value) : 0))
const refundFee = computed(() => num(refundRow.value?.application_fee))
const partialAmountError = computed(() => {
	if (refundMode.value !== 'partial') return null
	const raw = String(refundAmountInput.value ?? '').trim().replace(',', '.')
	if (!raw) return 'Enter the amount to refund.'
	if (!/^\d+(\.\d+)?$/.test(raw)) return 'Enter an amount like 10 or 12.50.'
	const digits = fractionDigits(refundCurrency.value)
	if ((raw.split('.')[1] || '').length > digits) return digits ? `Use at most ${digits} decimals.` : 'Use a whole amount.'
	const n = Number(raw)
	const min = 1 / 10 ** digits
	if (n < min) return `The smallest refund is ${formatMoney(min, refundCurrency.value)}.`
	if (n > refundRemaining.value + 1e-9) return `At most ${formatMoney(refundRemaining.value, refundCurrency.value)} is left to refund.`
	return null
})
const refundSum = computed(() => (refundMode.value === 'partial' && !partialAmountError.value ? Number(String(refundAmountInput.value).trim().replace(',', '.')) : refundRemaining.value))
const refundIsWhole = computed(() => refundSum.value >= refundRemaining.value - 1e-9)
// Estimate only: Stripe returns the fee in proportion to each refund (all of it on a full refund).
const refundFeeBack = computed(() => {
	const amount = num(refundRow.value?.amount)
	if (!(refundFee.value > 0) || !(amount > 0) || partialAmountError.value) return 0
	const left = Math.max(0, refundFee.value - num(refundRow.value?.application_fee_refunded))
	const unit = 10 ** fractionDigits(refundCurrency.value)
	return refundIsWhole.value ? left : Math.min(left, Math.round((refundFee.value * refundSum.value * unit) / amount) / unit)
})

const openRefund = (d) => {
	if (!canRefund(d)) return
	refundRow.value = d
	refundMode.value = 'full'
	refundAmountInput.value = ''
	refundReason.value = 'requested_by_customer'
	refundError.value = null
	refundOpen.value = true
}

const REFUND_ERROR_TEXT = {
	donations_disabled: 'Donations are switched off on the payment service right now, so refunds are unavailable. Try again later.',
	stripe_inactive: 'Your Stripe account is not active, so Stripe cannot issue the refund. Finish connecting Stripe under Settings › General first.',
	donation_not_found: 'This donation no longer exists on the payment service.',
}
const describeRefundError = (err) => {
	if (err?.reason === 'stripe_error') return `Stripe could not refund this gift: ${String(err.message || 'unknown error').replace(/\.+$/, '')}.`
	return REFUND_ERROR_TEXT[err?.reason] || err?.message || 'Could not refund this donation.'
}

const confirmRefund = async () => {
	if (!refundRow.value || refunding.value || partialAmountError.value || refundRemaining.value <= 0) return
	const target = refundRow.value
	const id = target.public_id || target.id
	refunding.value = true
	refundError.value = null
	try {
		const body = { reason: refundReason.value }
		if (refundMode.value === 'partial') body.amount = refundSum.value
		const res = await SiteAdminService.refundDonation(props.siteId, id, body)
		const updated = res?.donation && typeof res.donation === 'object' ? { ...target, ...res.donation } : target
		rows.value = rows.value.map((r) => ((r.public_id || r.id) === id ? updated : r))
		const currency = res?.refund?.currency || target.currency
		const feeBack = num(res?.refund?.application_fee_refunded)
		const feeNote = feeBack > 0 ? ` Castr's ${formatMoney(feeBack, currency)} fee was returned to you.` : ''
		notify({ group: 'success', text: `${formatMoney(res?.refund?.amount ?? refundSum.value, currency)} refunded to ${donorLabel(target)}.${feeNote}` })
		refundOpen.value = false
	} catch (err) {
		refundError.value = describeRefundError(err)
		// Someone (or Stripe's dashboard) may have refunded it meanwhile: refresh what is left.
		if (['donation_not_refundable', 'invalid_refund_amount', 'donation_not_found'].includes(err?.reason)) load()
	}
	refunding.value = false
}
watch(rows, (list) => {
	if (!refundOpen.value || !refundRow.value) return
	const id = refundRow.value.public_id || refundRow.value.id
	const fresh = list.find((r) => (r.public_id || r.id) === id)
	if (fresh) refundRow.value = fresh
})
</script>

<template>
	<div data-testid="site-donations">
		<div class="mb-4 flex flex-wrap items-start justify-between gap-3">
			<div class="min-w-0 flex-1">
				<h2 class="mb-0.5 text-headline font-semibold text-surface-text-2">Donations</h2>
				<p class="mb-0 text-tiny text-surface-7">Gifts made on your site, newest first. Refund a gift in full or in part from its row.</p>
			</div>
			<FormButton type="secondary" size="sm" class="shrink-0" :to="{ name: 'SiteBroadcast', params: { siteId } }" data-testid="donations-settings-link">Giving settings</FormButton>
		</div>

		<p v-if="unlinked" class="mb-4 rounded-lg bg-surface-3 px-3 py-2.5 text-tiny text-surface-7" data-testid="donations-unlinked">
			Donations appear once the site is linked to your Paywall account — publish the site first.
		</p>
		<Alert v-else-if="loadError" type="error" class="mb-4">{{ loadError.message || 'Could not load the donations.' }}</Alert>

		<template v-if="!unlinked && !loadError">
			<div class="mb-4 flex flex-wrap items-end gap-2" data-testid="donations-filters">
				<div class="relative min-w-0 flex-1 sm:max-w-xs">
					<label for="donations-search" class="sr-only">Search donations by e-mail or name</label>
					<FormInput id="donations-search" v-model="q" type="search" placeholder="Search by e-mail or name" input-classes="pl-9 placeholder:text-surface-6" :disabled="Boolean(disabledReason)" />
					<icon-base class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-surface-7" :width="14" :height="14"><icon-search /></icon-base>
				</div>
				<FormSelect id="donations-status" v-model="status" :options="STATUSES" size="sm" class="!w-48" :disabled="Boolean(disabledReason)" />
			</div>

			<Spinner v-if="loading" text="Loading donations…" classes="text-surface-8 mt-24" spinner-color="var(--c-dark-8)" spinner-size="15px" />

			<EmptyState v-else-if="!rows.length" title="No donations yet" :subtitle="emptyCopy" min-height="40vh" data-testid="donations-empty" />

			<div v-else class="overflow-x-auto rounded-lg bg-surface-2 p-2">
				<table class="w-full min-w-[600px] border-separate border-spacing-y-1 text-left" data-testid="donations-table">
					<thead>
						<tr class="text-[11px] uppercase tracking-wider text-surface-6">
							<th class="whitespace-nowrap px-2.5 pb-1 font-semibold">Date</th>
							<th class="whitespace-nowrap px-2.5 pb-1 font-semibold">Donor</th>
							<th class="whitespace-nowrap px-2.5 pb-1 font-semibold">Amount</th>
							<th class="whitespace-nowrap px-2.5 pb-1 font-semibold">Status</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="d in rows" :key="d.public_id || d.id" class="group" :data-testid="`donation-${d.public_id || d.id}`" :data-status="d.status">
							<td class="rounded-l-lg bg-surface-3/60 px-2.5 py-2.5 align-middle">
								<span class="block text-nowrap text-sm text-surface-8">{{ fmtDate(d.created_at) }}</span>
								<span class="block text-nowrap text-tiny text-surface-7">{{ fmtTime(d.created_at) }}</span>
							</td>
							<td class="bg-surface-3/60 px-2.5 py-2.5 align-middle">
								<span v-if="donorName(d)" class="block break-words text-sm text-surface-text-1" data-testid="donation-donor-name">{{ donorName(d) }}</span>
								<span v-if="donorEmail(d)" class="flex items-center gap-1.5">
									<span :class="donorName(d) ? 'text-tiny text-surface-7' : 'text-sm text-surface-text-1'" data-testid="donation-email">{{ emailParts(donorEmail(d))[0] }}<wbr>{{ emailParts(donorEmail(d))[1] }}</span>
									<button type="button" class="inline-flex size-5 shrink-0 items-center justify-center rounded text-surface-7 hover:bg-surface-4 hover:text-white" :title="`Copy ${donorEmail(d)}`" :aria-label="`Copy ${donorEmail(d)}`" data-testid="donation-copy-email" @click="copyEmail(donorEmail(d))">
										<icon-base :width="12" :height="12" :width-view-box="16" :height-view-box="16" fill="none"><icon-copy /></icon-base>
									</button>
								</span>
								<span v-if="!donorName(d) && !donorEmail(d)" class="block text-sm text-surface-text-1">Donor</span>
								<span v-if="!d.viewer" class="mt-0.5 inline-block rounded bg-surface-4 px-1.5 py-0.5 text-[11px] text-surface-8" title="Gave without a viewer account">Guest</span>
							</td>
							<td class="bg-surface-3/60 px-2.5 py-2.5 align-middle">
								<span class="text-nowrap text-sm font-medium" :class="[d.status === 'succeeded' || d.status === 'partially_refunded' ? 'text-surface-text-1' : 'text-surface-6', d.status === 'refunded' && 'line-through']" data-testid="donation-amount">{{ amountLabel(d) }}</span>
								<span v-if="refundedOf(d) > 0" class="block text-nowrap text-tiny text-warning" :title="d.refunded_at ? `Last refund ${fmtDate(d.refunded_at)}` : undefined" data-testid="donation-refunded">{{ refundedNote(d) }}</span>
							</td>
							<td class="rounded-r-lg bg-surface-3/60 px-2.5 py-2.5 align-middle">
								<div class="flex flex-wrap items-center justify-between gap-2">
									<StatusPill pill size="sm" :variant="STATUS_VARIANTS[d.status] || 'muted'" data-testid="donation-status">{{ statusLabel(d) }}</StatusPill>
									<FormButton v-if="canRefund(d)" type="secondary" size="sm" :aria-label="`Refund ${donorLabel(d)}`" :disabled="refunding && refundRow === d" @click="openRefund(d)">Refund</FormButton>
								</div>
							</td>
						</tr>
					</tbody>
				</table>
			</div>

			<div v-if="meta.total > PER_PAGE" class="mt-4 flex justify-center">
				<FormPagination v-model="page" :total-rows="meta.total" :per-page="PER_PAGE" />
			</div>
		</template>

		<ModalBox v-model="refundOpen" title="Refund this gift?" size="sm" z-index="z-[60]">
			<div v-if="refundRow" class="flex flex-col gap-3 px-4 pb-4 sm:px-6 sm:pb-6" data-testid="donation-refund-modal">
				<Alert v-if="refundError" type="error" class="!mb-0" data-testid="donation-refund-error">{{ refundError }}</Alert>
				<dl class="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-lg bg-surface-3 px-3 py-2.5 text-sm">
					<dt class="text-surface-7">Donor</dt><dd class="m-0 min-w-0 text-white [overflow-wrap:anywhere]">{{ donorLabel(refundRow) }}<template v-if="donorName(refundRow) && donorEmail(refundRow)"> · {{ donorEmail(refundRow) }}</template></dd>
					<dt class="text-surface-7">Gift</dt><dd class="m-0 text-white">{{ formatMoney(refundRow.amount, refundCurrency) }} · {{ fmtDate(refundRow.created_at) }}</dd>
					<template v-if="refundedOf(refundRow) > 0">
						<dt class="text-surface-7">Refunded</dt><dd class="m-0 text-white">{{ formatMoney(refundedOf(refundRow), refundCurrency) }}</dd>
					</template>
					<dt class="text-surface-7">Left to refund</dt><dd class="m-0 text-white" data-testid="donation-refund-remaining">{{ formatMoney(refundRemaining, refundCurrency) }}</dd>
				</dl>

				<template v-if="refundRemaining > 0">
					<fieldset class="m-0 flex flex-col gap-2 border-0 p-0">
						<legend class="mb-2 text-tiny font-medium text-surface-text-1">Amount</legend>
						<label class="flex cursor-pointer items-center gap-2 text-sm text-surface-text-1">
							<input v-model="refundMode" type="radio" name="donation-refund-mode" value="full" class="size-3.5 accent-main" data-testid="donation-refund-full">
							{{ refundedOf(refundRow) > 0 ? 'Everything left' : 'Full amount' }} ({{ formatMoney(refundRemaining, refundCurrency) }})
						</label>
						<label class="flex cursor-pointer items-center gap-2 text-sm text-surface-text-1">
							<input v-model="refundMode" type="radio" name="donation-refund-mode" value="partial" class="size-3.5 accent-main" data-testid="donation-refund-partial">
							Partial amount
						</label>
						<div v-if="refundMode === 'partial'" class="pl-5">
							<FormInput id="donation-refund-amount" v-model="refundAmountInput" type="text" :label="`Amount to refund (${refundCurrency}, up to ${formatMoney(refundRemaining, refundCurrency)})`" :placeholder="String(refundRemaining)" :is-error="Boolean(refundAmountInput) && Boolean(partialAmountError)" input-classes="placeholder:text-surface-6" />
							<p v-if="refundAmountInput && partialAmountError" class="mb-0 mt-1 text-tiny text-error-light" data-testid="donation-refund-amount-error">{{ partialAmountError }}</p>
						</div>
					</fieldset>

					<FormSelect id="donation-refund-reason" v-model="refundReason" :options="REFUND_REASONS" label="Reason" size="sm" />
					<p v-if="refundReason === 'fraudulent'" class="mb-0 text-tiny text-surface-7">Stripe also adds the card and e-mail to your Radar block lists.</p>

					<p class="mb-0 rounded-lg bg-warning-transparent px-3 py-2.5 text-tiny text-surface-8" data-testid="donation-refund-fees">
						The donor gets {{ partialAmountError ? 'the refund' : formatMoney(refundSum, refundCurrency) }} back on their card through Stripe (usually within 5–10 business days); it comes out of your Stripe balance.
						Castr's platform fee is returned to you proportionally<template v-if="refundFeeBack > 0"> (about {{ formatMoney(refundFeeBack, refundCurrency) }})</template>; Stripe's processing fee isn't refundable.
						<template v-if="!refundIsWhole">You can refund the rest later.</template>
					</p>
				</template>
			</div>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton type="danger" class="w-full justify-center sm:w-auto" :disabled="refunding || Boolean(partialAmountError) || refundRemaining <= 0" :is-loading="refunding" data-testid="donation-refund-confirm" @click="confirmRefund">
						{{ partialAmountError ? 'Refund' : `Refund ${formatMoney(refundSum, refundCurrency)}` }}
					</FormButton>
					<FormButton type="secondary" class="w-full justify-center sm:w-auto" @click="refundOpen = false">Cancel</FormButton>
				</footer>
			</template>
		</ModalBox>
	</div>
</template>
